import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getCoopReportDetail } from '../api/crud';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import ReactApexChart from 'react-apexcharts';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
}));

const GradientCard = styled(Card)(({ theme, bgcolor }) => ({
  background: bgcolor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
}));

// Helper function สำหรับจัดสี chip ตามคะแนน
const getScoreChipStyle = (score, max = 10) => {
  const percentage = (score / max) * 100;
  
  if (percentage >= 90) {
    return { bgcolor: '#e8f5e9', color: '#2e7d32', borderColor: '#81c784' };
  } else if (percentage >= 80) {
    return { bgcolor: '#e3f2fd', color: '#1565c0', borderColor: '#64b5f6' };
  } else if (percentage >= 70) {
    return { bgcolor: '#fff8e1', color: '#f57f17', borderColor: '#ffd54f' };
  } else if (percentage >= 60) {
    return { bgcolor: '#fff3e0', color: '#e65100', borderColor: '#ffb74d' };
  } else {
    return { bgcolor: '#ffebee', color: '#c62828', borderColor: '#e57373' };
  }
};

const CoopReportVisualization = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [reqId, setReqId] = useState(searchParams.get('req_id') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!reqId) return;
      
      try {
        setLoading(true);
        setError(null);
        const result = await getCoopReportDetail(reqId);
        
        if (!result.success) {
          setError(result.error || 'ไม่พบข้อมูลการประเมิน');
          setData(null);
          return;
        }
        
        setData(result);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reqId]);

  // Radar Chart สำหรับหมวดหมู่หลัก
  const getRadarChartOptions = () => {
    if (!data || !data.criteria) return { series: [], options: {} };

    const categories = [
      {
        name: 'เนื้อหาและโครงสร้าง',
        items: data.criteria.filter(c => ['q1', 'q2', 'q3', 'q4', 'q5'].includes(c.id)),
        maxScore: 25 // 5+5+5+5+5
      },
      {
        name: 'ผลและวิเคราะห์',
        items: data.criteria.filter(c => ['q6', 'q7'].includes(c.id)),
        maxScore: 30 // 20+10
      },
      {
        name: 'สรุปและข้อเสนอแนะ',
        items: data.criteria.filter(c => ['q8', 'q9'].includes(c.id)),
        maxScore: 15 // 10+5
      },
      {
        name: 'รูปแบบและการเขียน',
        items: data.criteria.filter(c => ['q10', 'q11', 'q12'].includes(c.id)),
        maxScore: 20 // 10+5+5
      },
      {
        name: 'อ้างอิงและภาคผนวก',
        items: data.criteria.filter(c => ['q13', 'q14'].includes(c.id)),
        maxScore: 10 // 5+5
      }
    ];

    const categoryScores = categories.map(cat => {
      const totalScore = cat.items.reduce((sum, item) => sum + item.score, 0);
      return cat.maxScore > 0 ? (totalScore / cat.maxScore) * 10 : 0;
    });

    // Wrap long category names to reduce collision with data labels
    const categoryNames = categories.map(c => {
      const n = c.name || '';
      if (n.includes('และ')) {
        // insert a line break at the first occurrence of 'และ'
        return n.replace('และ', 'และ\n');
      }
      // Fallback: split roughly in half if very long
      if (n.length > 12) {
        const mid = Math.floor(n.length / 2);
        return n.slice(0, mid) + '\n' + n.slice(mid);
      }
      return n;
    });

    return {
      series: [{
        name: 'คะแนนเฉลี่ย',
        data: categoryScores.map(s => parseFloat(s.toFixed(2)))
      }],
      options: {
        chart: {
          type: 'radar',
          toolbar: { show: false }
        },
        xaxis: {
          categories: categoryNames,
          labels: {
            show: true,
            style: {
              fontSize: '12px',
              fontFamily: 'Sarabun, sans-serif',
              fontWeight: 500
            },
            offsetY: 4
          }
        },
        yaxis: {
          show: true,
          min: 0,
          max: 10,
          tickAmount: 5,
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        plotOptions: {
          radar: {
            size: 140,
            offsetY: 10,
            polygons: {
              strokeColors: '#e0e7ff',
              strokeWidth: 1.5,
              fill: {
                colors: ['#f8faff', '#ffffff']
              }
            }
          }
        },
        colors: ['#6d28d9'],
        markers: {
          size: 6,
          colors: ['#6d28d9'],
          strokeColor: '#fff',
          strokeWidth: 2,
          hover: {
            size: 8
          }
        },
        fill: {
          opacity: 0.35
        },
        stroke: {
          width: 3,
          colors: ['#6d28d9']
        },
        legend: {
          show: false
        },
        dataLabels: {
          enabled: true,
          background: {
            enabled: true,
            borderRadius: 4,
            padding: 5,
            opacity: 0.95,
            borderWidth: 1,
            borderColor: '#6d28d9'
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#1e293b']
          }
        },
        tooltip: {
          y: {
            formatter: function(val, { seriesIndex, dataPointIndex }) {
              const category = categories[dataPointIndex];
              return `${val.toFixed(2)}/10 คะแนน`;
            }
          }
        }
      }
    };
  };

  const handleSearch = () => {
    navigate(`/coop-report-evaluation?req_id=${reqId}`);
    window.location.reload();
  };

  const handlePrint = () => {
    window.print();
  };

  // ฟังก์ชันสำหรับแยกรหัสนิสิต 8 ตัวแรกจาก req_id (ใช้เหมือนหน้า InternshipEvaluation)
  const getStudentId = (reqId) => {
    return reqId ? reqId.toString().substring(0, 8) : '';
  };

  // ฟังก์ชันสำหรับสร้าง URL รูปนิสิต
  const getStudentImageUrl = (reqId) => {
    const studentId = getStudentId(reqId);
    return studentId
      ? `https://reg.buu.ac.th/registrar/getstudentimage.asp?id=${studentId}`
      : `${process.env.PUBLIC_URL}/assets/images/logo.png`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Search Section */}
      <StyledCard sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              ผลการประเมินรายงานสหกิจศึกษา
            </Typography>
            <TextField
              size="small"
              label="Request ID"
              value={reqId}
              onChange={(e) => setReqId(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button variant="contained" onClick={handleSearch}>
              ค้นหา
            </Button>
          </Box>
        </CardContent>
      </StyledCard>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data && (
        <Box>
          {/* Page Header */}
          <Box 
            sx={{ 
              bgcolor: '#a5b4fc',
              borderRadius: 2,
              p: 3,
              mb: 4,
              boxShadow: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#4c1d95', mb: 0.5 }}>
                  ผลการประเมินรายงานสหกิจศึกษา
                </Typography>
                <Typography variant="body2" sx={{ color: '#5b21b6' }}>
                  คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา
                </Typography>
              </Box>
              {data.header.req_id && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    bgcolor: 'rgba(76, 29, 149, 0.15)', 
                    px: 2.5,
                    py: 1,
                    borderRadius: 1.5,
                    border: '1px solid rgba(76, 29, 149, 0.2)'
                  }}>
                    <Typography variant="caption" sx={{ color: '#5b21b6', display: 'block', fontSize: '0.7rem' }}>
                      REQ ID
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4c1d95', lineHeight: 1.2 }}>
                      {data.header.req_id}
                    </Typography>
                  </Box>
                  <IconButton 
                    onClick={handlePrint} 
                    className="no-print"
                    sx={{ 
                      bgcolor: 'white',
                      color: '#6d28d9',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.9)'
                      }
                    }}
                  >
                    <PrintIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>

          {/* Student and Evaluator Info */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Card sx={{ 
                borderRadius: 2, 
                boxShadow: 2,
                height: '100%'
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#a5b4fc',
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AssignmentIcon sx={{ color: '#4c1d95', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#6d28d9' }}>
                      ข้อมูลนิสิต
                    </Typography>
                  </Box>
                  <Box sx={{ pl: 0.5 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                            ชื่อ-นามสกุล
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {data.header.student_name}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                            สาขาวิชา
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {data.header.student_program}
                          </Typography>
                        </Box>
                        {data.header.organization_dept && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                              แผนก/ฝ่าย
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {data.header.organization_dept}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flexShrink: 0, ml: { xs: 0, sm: 1 } }}>
                        <img
                          src={getStudentImageUrl(data.header.req_id)}
                          alt="รูปนิสิต"
                          style={{
                            width: 80,
                            height: 100,
                            objectFit: 'cover',
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                          onError={(e) => {
                            e.target.src = `${process.env.PUBLIC_URL}/assets/images/logo.png`;
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ 
                borderRadius: 2, 
                boxShadow: 2,
                height: '100%'
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: 'success.main',
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DescriptionIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                      ผู้ประเมิน
                    </Typography>
                  </Box>
                  <Box sx={{ pl: 0.5 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        ชื่อผู้ประเมิน
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {data.header.evaluator_name}
                      </Typography>
                    </Box>
                    {data.header.submitted_at && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          วันที่ประเมิน
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {new Date(data.header.submitted_at).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Score Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={5}>
              <Card sx={{ 
                bgcolor: '#a5b4fc',
                color: '#4c1d95',
                borderRadius: 2,
                boxShadow: 3
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                        คะแนนรวม
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                        (เต็ม {data.scores.max} คะแนน)
                      </Typography>
                    </Box>
                    <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                      {Math.round(data.scores.total)}
                    </Typography>
                  </Box>
                  
                  {/* Star Rating */}
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {[...Array(5)].map((_, i) => {
                      const starValue = (data.scores.total / data.scores.max) * 5;
                      const isFull = i < Math.floor(starValue);
                      const isHalf = i === Math.floor(starValue) && starValue % 1 >= 0.5;
                      
                      return (
                        <Box
                          key={i}
                          sx={{
                            fontSize: '2rem',
                            color: '#fbbf24',
                            position: 'relative',
                            display: 'inline-block',
                          }}
                        >
                          {isHalf ? (
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                              <Box component="span" sx={{ opacity: 0.3, color: '#4c1d95' }}>★</Box>
                              <Box
                                component="span"
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  width: '50%',
                                  overflow: 'hidden',
                                  opacity: 1,
                                  color: '#fbbf24',
                                }}
                              >
                                ★
                              </Box>
                            </Box>
                          ) : (
                            <Box component="span" sx={{ opacity: isFull ? 1 : 0.3, color: isFull ? '#fbbf24' : '#4c1d95' }}>
                              ★
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>

              {/* Project Title */}
              {data.header.project_title && (
                <Card sx={{ 
                  mt: 3, 
                  borderRadius: 2,
                  boxShadow: 2,
                  borderLeft: '4px solid',
                  borderColor: '#a5b4fc'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Box sx={{ 
                        bgcolor: '#a5b4fc',
                        color: '#4c1d95',
                        p: 1.5,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 48,
                        height: 48
                      }}>
                        <AssignmentIcon />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                          หัวข้อโครงงาน
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#6d28d9', lineHeight: 1.6 }}>
                          {data.header.project_title}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Comments Card */}
              {data.comments && (
                <Card sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  boxShadow: 2,
                  borderLeft: '4px solid',
                  borderColor: '#a5b4fc'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Box sx={{ 
                        bgcolor: '#a5b4fc',
                        color: '#4c1d95',
                        p: 1.5,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 48,
                        height: 48
                      }}>
                        <DescriptionIcon />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                          ข้อคิดเห็นเพิ่มเติม
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#5b21b6' }}>
                          {data.comments}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={7}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 2, 
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                    ภาพรวมการประเมินตามหมวดหมู่
                  </Typography>
                  <ReactApexChart
                    options={getRadarChartOptions().options}
                    series={getRadarChartOptions().series}
                    type="radar"
                    height={380}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Criteria Details */}
          <Card sx={{ 
            mb: 4,
            borderRadius: 2, 
            boxShadow: 2
          }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  รายละเอียดการประเมิน
                </Typography>
              </Box>
              
              <Grid container>
                {/* Column 1: Items 1-7 */}
                <Grid item xs={12} md={6}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%', borderBottom: '2px solid #e0e0e0', py: 1 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '60%', borderBottom: '2px solid #e0e0e0', py: 1 }}>หัวข้อการประเมิน</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '25%', borderBottom: '2px solid #e0e0e0', py: 1 }}>คะแนน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.criteria.slice(0, 7).map((item, index) => (
                          <TableRow 
                            key={item.id} 
                            sx={{ 
                              '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                          >
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '50%',
                                bgcolor: '#a5b4fc',
                                color: '#4c1d95',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}>
                                {index + 1}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, fontWeight: 500, fontSize: '0.875rem' }}>
                              {item.name}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.5 }}>
                              <Chip
                                label={`${Math.round(item.score)}/${item.max}`}
                                size="small"
                                sx={{
                                  ...getScoreChipStyle(item.score, item.max),
                                  fontWeight: 'bold',
                                  fontSize: '0.75rem',
                                  minWidth: 65,
                                  height: 24
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Column 2: Items 8-14 */}
                <Grid item xs={12} md={6}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%', borderBottom: '2px solid #e0e0e0', py: 1 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '60%', borderBottom: '2px solid #e0e0e0', py: 1 }}>หัวข้อการประเมิน</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '25%', borderBottom: '2px solid #e0e0e0', py: 1 }}>คะแนน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.criteria.slice(7, 14).map((item, index) => (
                          <TableRow 
                            key={item.id} 
                            sx={{ 
                              '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                          >
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '50%',
                                bgcolor: '#a5b4fc',
                                color: '#4c1d95',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}>
                                {index + 8}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, fontWeight: 500, fontSize: '0.875rem' }}>
                              {item.name}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.5 }}>
                              <Chip
                                label={`${Math.round(item.score)}/${item.max}`}
                                size="small"
                                sx={{
                                  ...getScoreChipStyle(item.score, item.max),
                                  fontWeight: 'bold',
                                  fontSize: '0.75rem',
                                  minWidth: 65,
                                  height: 24
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

        </Box>
      )}
    </Container>
  );
};

export default CoopReportVisualization;
